import React, { useState, useEffect, useContext } from "react";
import { Select, Cascader } from "antd";
import DatePicker from "react-multi-date-picker";
import { LoadingIcon } from "../../components";
import {
  getOrgs,
  getSites,
  getCrops,
  getHarvestDates,
  getPlots,
} from "../../utils/api";
import { UserContext } from "../../context/UserContext";
import { useLocalStorage } from "../../hooks";
import { COLORS } from "../../consts";
import { TopDropdown } from "../../components";
import { useSearchParams } from "react-router-dom";

const Dropdowns = ({
  setSiteMetadata,
  currentMetric,
  setCurrentMetric,
  metricType,
  setMetricType,
  fetchGraphData,
  currentOrg,
  setCurrentOrg,
  currentSite,
  setCurrentSite,
  currentPlot,
  setCurrentPlot,
  currentCrop,
  setCurrentCrop,
  currentDate,
  setCurrentDate,
  setDropdownLoading,
}) => {
  const [defaultDropdown, setDefaultDropdown] = useLocalStorage(
    "dropdowns",
    null,
  );
  const [analysisPlots, setAnalysisPlots] = useLocalStorage(
    "analysisPlots",
    null,
  );

  const { userData } = useContext(UserContext);

  const [orgData, setOrgData] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);

  const [siteData, setSiteData] = useState([]);
  const [siteLoading, setSiteLoading] = useState(false);

  const [plotData, setPlotData] = useState([]);
  const [plotLoading, setPlotLoading] = useState(false);

  const [cropData, setCropData] = useState([]);
  const [cropLoading, setCropLoading] = useState(false);

  const [dateData, setDateData] = useState([]);
  const [metricData, setMetricData] = useState([]);

  const metricValToLabel = { sum: "Cumulative", avg: "Average" };
  const [settingDefaults, setSettingDefaults] = useState({});

  const [searchParams, setSearchParams] = useSearchParams();

  // -----------------------------------------
  //  API Functions
  // -----------------------------------------
  const fetchOrgs = async () => {
    try {
      setOrgLoading(true);
      const res = await getOrgs();
      const newData = res?.data?.data
        .filter((org) => org.is_active && org.is_breeder)
        .map((org) => ({
          name: org.name,
          id: org.id,
        }));
      setOrgData(newData);
    } catch (err) {
      console.log({ err });
    } finally {
      setOrgLoading(false);
    }
  };

  const fetchSites = async (orgId) => {
    try {
      setSiteLoading(true);
      const res = await getSites(orgId);
      const data = res?.data?.data
        .filter((s) => s.is_active)
        .map((s) => ({
          name: s.name,
          displayName: s.display_name,
          id: s.id,
          metadata: s.metadata,
        }));
      setSiteData(data);
    } catch (err) {
      console.log({ err });
    } finally {
      setSiteLoading(false);
    }
  };

  const fetchCrops = async (siteId) => {
    try {
      setCropLoading(true);
      const res = await getCrops(siteId);
      const cropList = Array.isArray(res?.data?.data) ? res.data.data : [];
      const data = cropList.filter((c) => c.is_active).map((c) => ({
        name: c.name,
        displayName: c.display_name,
        id: c.id,
      }));
      setCropData(data);
    } catch (err) {
      console.log({ err });
    } finally {
      setCropLoading(false);
    }
  };

  const fetchPlots = async (orgId, siteId, cropId = null) => {
    try {
      setPlotLoading(true);
      const res = await getPlots(orgId, siteId, cropId);
      const plotList = Array.isArray(res?.data?.data) ? res.data.data : [];
      const data = plotList.map((plot) => ({
        name: plot.name,
        id: plot.id,
      }));
      setPlotData(data);
    } catch (err) {
      console.log({ err });
    } finally {
      setPlotLoading(false);
    }
  };

  const fetchHarvestDates = async (orgId, siteId, cropId) => {
    try {
      const res = await getHarvestDates(orgId, siteId, cropId);
      const dateList = Array.isArray(res?.data?.data) ? res.data.data : [];
      const dates = dateList.map((d) => d.date);
      setDateData(dates);
    } catch (err) {
      console.log({ err });
    }
  };

  // -----------------------------------------
  //  On mount: fetch orgs or set org
  // -----------------------------------------
  useEffect(() => {
    const fetchData = async () => {
      if (userData) {
        try {
          if (userData.is_superuser) {
            await fetchOrgs();
          } else {
            setCurrentOrg(userData.organization_id);

            const res = await getSites(userData.organization_id);
            const siteList = Array.isArray(res?.data?.data)
              ? res.data.data
              : [];
            const filtered = siteList.filter((s) => s.is_active);
            const mapped = filtered.map((s) => ({
              name: s.name,
              displayName: s.display_name,
              id: s.id,
              metadata: s.metadata,
            }));
            setSiteData(mapped);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      }
    };
    fetchData();
  }, [userData]);

  // If superuser => fetch sites on org change
  useEffect(() => {
    if (currentOrg && (userData?.is_superuser || userData?.isAdmin)) {
      fetchSites(currentOrg);
    }
  }, [currentOrg]);

  // Whenever site changes => fetch crops
  useEffect(() => {
    if (currentSite) {
      fetchCrops(currentSite.id);
    } else {
      setCurrentCrop(null);
    }
  }, [currentSite]);

  // Whenever site or crop changes => fetch plots
  useEffect(() => {
    if (currentSite && currentOrg) {
      fetchPlots(currentOrg, currentSite?.id, currentCrop?.id);
    }
  }, [currentSite]);

  // If org/site/crop => fetch harvest dates
  useEffect(() => {
    if (currentSite && currentOrg && currentCrop) {
      fetchHarvestDates(currentOrg, currentSite.id, currentCrop.id);
    }
  }, [currentCrop, currentSite]);

  // -----------------------------------------
  //  If site/crop/plot/date => set siteMetadata, fetchGraphData
  // -----------------------------------------
  useEffect(() => {
    if (currentSite && currentCrop && currentPlot && currentDate) {
      setSiteMetadata(currentSite.metadata[currentCrop.name] || {});
      const metrics = currentSite.metadata[currentCrop.name]?.metrics
        ? Object.entries(currentSite.metadata[currentCrop.name]?.metrics).map(
            ([type, arr]) => ({
              type,
              metrics: arr,
            }),
          )
        : [];
      setMetricData(metrics);
      fetchGraphData();
    } else {
      setMetricData([]);
    }
  }, [currentSite, currentCrop, currentPlot, currentDate]);

  // -----------------------------------------
  //  Sync multi‐plot and multi‐date to query param
  // -----------------------------------------
  useEffect(() => {
    if (currentPlot && currentPlot.length) {
      const params = new URLSearchParams(searchParams);
      const plotIds = currentPlot.map((p) => p.id).join(",");
      params.set("plots", plotIds);
      setSearchParams(params);
    }
  }, [currentPlot]);

  useEffect(() => {
    if (currentDate && currentDate.length) {
      const params = new URLSearchParams(searchParams);
      // store them as a CSV
      params.set("dates", currentDate.join(","));
      setSearchParams(params);
    }
  }, [currentDate]);

  // -----------------------------------------
  //  ID-based defaults for org, site, crop, plot
  // -----------------------------------------
  // Org
  useEffect(() => {
    if (orgData?.length) {
      const params = new URLSearchParams(searchParams);
      const orgIdFromUrl = params.get("org");

      if (orgIdFromUrl) {
        const foundOrg = orgData.find((o) => o.id === orgIdFromUrl);
        setCurrentOrg(
          foundOrg ? foundOrg.id : defaultDropdown?.org || orgData[0].id,
        );
      } else if (!settingDefaults.org) {
        setCurrentOrg(defaultDropdown?.org || orgData[0]?.id);
        setSettingDefaults((prev) => ({ ...prev, org: true }));
      } else {
        setCurrentOrg(orgData[0]?.id);
      }
    }
  }, [orgData]);

  // Site
  useEffect(() => {
    if (siteData?.length) {
      const params = new URLSearchParams(searchParams);
      const siteIdFromUrl = params.get("site");
      if (siteIdFromUrl) {
        const foundSite = siteData.find((s) => s.id === siteIdFromUrl);
        setCurrentSite(foundSite || siteData[0]);
      } else if (!settingDefaults.site) {
        const storedSiteId = defaultDropdown?.[currentOrg]?.site;
        if (storedSiteId) {
          const found = siteData.find((s) => s.id === storedSiteId.id); // old approach
          setCurrentSite(found || siteData[0]);
        } else {
          setCurrentSite(siteData[0]);
        }
        setSettingDefaults((prev) => ({ ...prev, site: true }));
      } else {
        setCurrentSite(siteData[0]);
      }
    } else {
      setCurrentSite(null);
    }
  }, [siteData]);

  // Crop
  useEffect(() => {
    if (cropData?.length) {
      const params = new URLSearchParams(searchParams);
      const cropIdFromUrl = params.get("crop");
      if (cropIdFromUrl) {
        const found = cropData.find((c) => c.id === cropIdFromUrl);
        setCurrentCrop(found || cropData[0]);
      } else if (!settingDefaults.crop) {
        const storedCropVal = defaultDropdown?.[currentOrg]?.crop;
        if (storedCropVal) {
          const found = cropData.find((c) => c.id === storedCropVal.id);
          setCurrentCrop(found || cropData[0]);
        } else {
          setCurrentCrop(cropData[0]);
        }
        setSettingDefaults((prev) => ({ ...prev, crop: true }));
      } else {
        setCurrentCrop(cropData[0]);
      }
    } else {
      setCurrentCrop(null);
    }
  }, [cropData]);

  // Plot (multi‐select)
  useEffect(() => {
    if (plotData?.length) {
      const params = new URLSearchParams(searchParams);
      const plotCsv = params.get("plots");
      if (plotCsv) {
        const plotIds = plotCsv.split(",");
        const selected = plotData.filter((p) => plotIds.includes(p.id));
        if (selected?.length) {
          setCurrentPlot(selected);
        } else {
          setCurrentPlot([plotData[0]]);
        }
      } else if (analysisPlots && analysisPlots[currentOrg]) {
        setCurrentPlot(analysisPlots[currentOrg]);
        if (!settingDefaults.plot) {
          setSettingDefaults((prev) => ({ ...prev, plot: true }));
        }
      } else {
        // fallback => first
        setCurrentPlot([plotData[0]]);
      }
    } else {
      setCurrentPlot(null);
    }
  }, [plotData]);

  // Date (multi‐select)
  useEffect(() => {
    if (dateData?.length) {
      // read "dates" param => e.g. "2023-01-01,2023-01-02"
      const params = new URLSearchParams(searchParams);
      const datesFromUrl = params.get("dates");
      let selectedDates = [];
      if (datesFromUrl) {
        const arr = datesFromUrl.split(",");
        selectedDates = dateData.filter((d) => arr.includes(d));
        if (!selectedDates.length) {
          selectedDates = [dateData[0]];
        }
      } else {
        selectedDates = [dateData[0]];
      }
      setCurrentDate(selectedDates);
      params.set("dates", selectedDates.join(","));
      setSearchParams(params);
    } else {
      setCurrentDate(null);
    }
  }, [dateData]);

  // -----------------------------------------
  //  Metric defaults
  // -----------------------------------------
  useEffect(() => {
    if (currentSite && currentCrop) {
      const params = new URLSearchParams(searchParams);
      const metricTypeFromUrl = params.get("metricType");
      let val;
      if (metricTypeFromUrl) {
        val = metricTypeFromUrl;
      } else {
        val =
          currentSite.metadata[currentCrop.name]?.default_metrics?.[0] || null;
      }
      setMetricType(val);
    } else {
      setMetricType(null);
    }
  }, [currentSite, currentCrop]);

  useEffect(() => {
    if (currentSite && currentCrop) {
      const params = new URLSearchParams(searchParams);
      const metricFromUrl = params.get("metric");
      let val;
      if (metricFromUrl) {
        try {
          val = JSON.parse(metricFromUrl);
        } catch {
          val = metricFromUrl;
        }
      } else {
        val =
          currentSite.metadata[currentCrop.name]?.default_metrics?.[1] || null;
      }
      setCurrentMetric(val);
    } else {
      setCurrentMetric(null);
    }
  }, [currentSite, currentCrop]);

  // -----------------------------------------
  //  Sync changes to localStorage + URL
  // -----------------------------------------
  // Org
  useEffect(() => {
    if (currentOrg) {
      const params = new URLSearchParams(searchParams);
      params.set("org", currentOrg);
      setSearchParams(params);

      setDefaultDropdown({
        ...defaultDropdown,
        org: currentOrg,
      });
    }
  }, [currentOrg]);

  // Site
  useEffect(() => {
    if (currentSite) {
      const params = new URLSearchParams(searchParams);
      params.set("site", currentSite.id);
      setSearchParams(params);

      const orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          site: currentSite.id,
        },
      });
    }
  }, [currentSite]);

  // Crop
  useEffect(() => {
    if (currentCrop) {
      const params = new URLSearchParams(searchParams);
      params.set("crop", currentCrop.id);
      setSearchParams(params);

      const orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          crop: currentCrop.id,
        },
      });
    }
  }, [currentCrop]);

  // Plot (multi)
  useEffect(() => {
    if (currentPlot && currentPlot.length) {
      setAnalysisPlots({
        ...analysisPlots,
        [currentOrg]: currentPlot,
      });
    }
  }, [currentPlot]);

  // Metric
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (currentMetric) {
      params.set("metric", JSON.stringify(currentMetric));
    }
    if (metricType) {
      params.set("metricType", metricType);
    }
    setSearchParams(params);
  }, [currentMetric, metricType]);

  // -----------------------------------------
  //  Loading
  // -----------------------------------------
  useEffect(() => {
    setDropdownLoading(orgLoading || siteLoading || cropLoading || plotLoading);
  }, [orgLoading, siteLoading, cropLoading, plotLoading, setDropdownLoading]);

  // Helper
  const isDictionary = (variable) => {
    return (
      typeof variable === "object" &&
      variable !== null &&
      !Array.isArray(variable)
    );
  };

  // -----------------------------------------
  //  Render
  // -----------------------------------------
  return (
    <>
      {orgLoading || siteLoading || plotLoading || cropLoading ? (
        <LoadingIcon height="100px" />
      ) : (
        <div className="w-full flex flex-wrap gap-4 justify-between sm:flex-col mb-10 dropdowns !font-MuseoSans">
          {userData?.is_superuser && (
            <TopDropdown
              label="Select Org:"
              val={currentOrg ?? undefined}
              classString="flex-1 min-w-[250px] sm:mb-2"
              placeholder="Select Organization"
              changeCallback={setCurrentOrg} // ID
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              filterSort={(a, b) =>
                (a?.label ?? "")
                  .toLowerCase()
                  .localeCompare((b?.label ?? "").toLowerCase())
              }
              notFound={orgLoading ? <LoadingIcon /> : null}
              data={orgData.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
            />
          )}

          <TopDropdown
            label="Select Site:"
            val={currentSite?.id ?? undefined}
            classString="flex-1 min-w-[250px] sm:mb-2"
            placeholder="Select Site"
            changeCallback={(siteId) => {
              const siteObj = siteData.find((s) => s.id === siteId);
              setCurrentSite(siteObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(a, b) =>
              (a?.label ?? "")
                .toLowerCase()
                .localeCompare((b?.label ?? "").toLowerCase())
            }
            notFound={siteLoading ? <LoadingIcon /> : null}
            data={siteData?.map((site) => ({
              value: site.id,
              label: site.displayName,
            }))}
          />

          {/* Crop => ID-based */}
          <TopDropdown
            label="Select Crop:"
            val={currentCrop?.id ?? undefined}
            placeholder="Select Crop"
            classString="flex-1 min-w-[250px] sm:mb-2"
            changeCallback={(cropId) => {
              const cropObj = cropData.find((c) => c.id === cropId);
              setCurrentCrop(cropObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(a, b) =>
              (a?.label ?? "")
                .toLowerCase()
                .localeCompare((b?.label ?? "").toLowerCase())
            }
            data={cropData?.map((crop) => ({
              value: crop.id,
              label: crop.displayName,
            }))}
          />

          {/* Plot => multi-select, ID-based */}
          <TopDropdown
            label="Select Plot:"
            val={currentPlot?.map((p) => p.id) ?? []}
            placeholder="Select Plot"
            mode="multiple"
            maxTagCount="responsive"
            classString="flex-1 min-w-[250px] sm:mb-2"
            changeCallback={(ids) => {
              const selectedPlots = plotData.filter((p) => ids.includes(p.id));
              setCurrentPlot(selectedPlots);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(a, b) =>
              (a?.label ?? "")
                .toLowerCase()
                .localeCompare((b?.label ?? "").toLowerCase())
            }
            data={plotData?.map((plot) => ({
              value: plot.id,
              label: plot.name,
            }))}
          />

          {/* Dates => multi-select with react-multi-date-picker */}
          <div className="flex flex-col sm:mb-2">
            <div className="text-sm text-darkerGray max-w-full mb-1">
              Select Dates:
            </div>
            <DatePicker
              value={currentDate}
              containerClassName="!grid flex-1 min-w-[250px]"
              onChange={(dates) => {
                // Convert to array of 'YYYY-MM-DD' strings
                const selected = dates.map((d) => {
                  const year = d.year;
                  const month =
                    d.month.number < 10 ? `0${d.month.number}` : d.month.number;
                  const day = d.day < 10 ? `0${d.day}` : d.day;
                  return `${year}-${month}-${day}`;
                });
                setCurrentDate(selected);
              }}
              multiple
              format="YYYY-MM-DD"
              mapDays={({ date }) => {
                const y = date.year;
                const m =
                  date.month.number < 10
                    ? `0${date.month.number}`
                    : date.month.number;
                const dd = date.day < 10 ? `0${date.day}` : date.day;
                const fullDate = `${y}-${m}-${dd}`;

                const isDisabled = !dateData.includes(fullDate);
                if (isDisabled) {
                  return {
                    disabled: true,
                    style: { color: COLORS.gray },
                  };
                }
              }}
            />
          </div>

          {/* Metric => Cascader */}
          <div className="flex flex-col">
            <div className="text-sm text-darkerGray max-w-full mb-1">
              Select Metric:
            </div>
            <Cascader
              value={
                metricType && currentMetric
                  ? [
                      metricType,
                      isDictionary(currentMetric)
                        ? currentMetric.value
                        : currentMetric,
                    ]
                  : null
              }
              placeholder="Select Metric"
              className="flex-1 min-w-[250px]"
              onChange={(val) => {
                // val = [newMetricType, newMetricValue]
                const [newType, newVal] = val;
                setMetricType(newType);

                const metricObj = metricData.find((m) => m.type === newType);
                if (!metricObj) {
                  setCurrentMetric(null);
                  return;
                }
                const foundMetric = metricObj.metrics.find((m) => {
                  if (typeof m === "string") {
                    return m === newVal;
                  } else if (isDictionary(m)) {
                    return m.value === newVal;
                  }
                  return false;
                });
                setCurrentMetric(
                  foundMetric || { value: "unknown", label: "Unknown Metric" },
                );
              }}
              options={metricData?.map((metricObj) => ({
                value: metricObj.type,
                label: metricValToLabel[metricObj.type] || metricObj.type,
                children: metricObj.metrics.map((metric) => {
                  if (typeof metric === "string") {
                    return {
                      value: metric,
                      label: metric,
                    };
                  } else if (isDictionary(metric)) {
                    return {
                      value: metric.value,
                      label: metric.label,
                    };
                  } else {
                    return {
                      value: "unknown",
                      label: "Unknown Metric",
                    };
                  }
                }),
              }))}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Dropdowns;
